import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { formatLocalDate } from '@mummssoftware/utils/formatters';
import {
  getStoppedMedications,
  getActiveMedications,
} from '@mummssoftware/utils/medications/utils';
import {
  HTTP_STATUS_OK,
  STRING_TRUE,
  MAINT_ORDER_TYPE_VERBAL,
  MAINT_ORDER_TYPE_ADMIT,
  RCOPIA_STOPREASON_DELETED,
  MED_IS_CURRENT_VERSION_FALSE,
} from '@mummssoftware/utils/constants';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import { messages } from '../Sections/Medication/messages';
import { MILLISECONDS_IN_DAY } from '../App/constants';

const parsedDateTime = (value) =>
  value ? parseDateMMDDYYYYSlashes(value) : null;

const compareParsedDateTime = (a, b) => {
  const aDate = parsedDateTime(a);
  const bDate = parsedDateTime(b);
  if (!aDate) return bDate ? 1 : 0;
  if (!bDate) return -1;
  return aDate - bDate;
};

const byDateField = (field, ascending) =>
  ascending
    ? (a, b) => compareParsedDateTime(a[field], b[field])
    : (a, b) => compareParsedDateTime(b[field], a[field]);

export const getApprovedMedications = (meds) =>
  meds.filter((med) => med.reconcileddate.length);

export const sortMedications = (
  meds,
  { name, sig, quantity, start, stop, covered, approved },
) => {
  if (sig.sort) {
    if (sig.asec) {
      return meds.sort((a, b) =>
        a.sig.toLowerCase() > b.sig.toLowerCase() ? 1 : -1,
      );
    }
    return meds.sort((a, b) =>
      a.sig.toLowerCase() < b.sig.toLowerCase() ? 1 : -1,
    );
  } else if (covered.sort) {
    if (covered.asec) {
      return meds.sort((a, b) =>
        a.coveredbyhospice.toLowerCase() > b.coveredbyhospice.toLowerCase()
          ? 1
          : -1,
      );
    }
    return meds.sort((a, b) =>
      a.coveredbyhospice.toLowerCase() < b.coveredbyhospice.toLowerCase()
        ? 1
        : -1,
    );
  } else if (quantity.sort) {
    if (quantity.asec) {
      return meds.sort((a, b) =>
        Number(a.quantity) > Number(b.quantity) ? 1 : -1,
      );
    }
    return meds.sort((a, b) =>
      Number(a.quantity) < Number(b.quantity) ? 1 : -1,
    );
  } else if (start.sort) {
    return meds.sort(byDateField('startdate_shifted', start.asec));
  } else if (stop.sort) {
    return meds.sort(byDateField('stopdate_shifted', stop.asec));
  } else if (approved.sort) {
    return meds.sort(byDateField('stopdate_shifted', approved.asec));
  } else if (name.sort) {
    if (name.asec) {
      return meds.sort((a, b) =>
        a.drugdescription.toLowerCase() > b.drugdescription.toLowerCase()
          ? 1
          : -1,
      );
    }
    return meds.sort((a, b) =>
      a.drugdescription.toLowerCase() < b.drugdescription.toLowerCase()
        ? 1
        : -1,
    );
  }
  return meds.sort((a, b) =>
    a.drugdescription.toLowerCase() > b.drugdescription.toLowerCase() ? 1 : -1,
  );
};

export const filterMedications = (
  medications,
  { to, from, toSearch, sortParams, filterParam, type },
) => {
  let medicationsToUse = [...medications];
  // filter results by type
  if (type === 'active') {
    medicationsToUse =
      medicationsToUse?.length &&
      medicationsToUse.filter(
        (m) =>
          !m.stopdate_shifted ||
          parseDateMMDDYYYYSlashes(m.stopdate_shifted) >= new Date(),
      );
  } else if (type === 'needsApproval') {
    medicationsToUse =
      medicationsToUse?.length &&
      medicationsToUse.filter((m) => isNeedsApprovalMed(m));
  } else if (type === 'recent') {
    // get all meds from the last 7 days
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    medicationsToUse =
      medicationsToUse?.length &&
      medicationsToUse.filter(
        (m) =>
          m.startdate_shifted &&
          parseDateMMDDYYYYSlashes(m.startdate_shifted) >=
            new Date(sevenDaysAgo),
      );
  } else if (type === 'byDoc') {
    medicationsToUse =
      medicationsToUse?.length &&
      medicationsToUse.filter((m) => m.orderingphysicianname === filterParam);
  }

  // filter results by text if toSearch has a value
  if (toSearch) {
    medicationsToUse =
      medicationsToUse?.length &&
      medicationsToUse.filter((m) =>
        m.drugdescription.toLowerCase().includes(toSearch.toLowerCase()),
      );
  }
  // filter results by date range
  const unsortedMeds =
    (medicationsToUse?.length &&
      medicationsToUse.filter((m) => {
        const startDate = m.startdate_shifted;
        const isBeforeTo =
          to && startDate
            ? parsedDateTime(startDate) <= parsedDateTime(to)
            : true;
        const isAfterFrom =
          from && m.stopdate_shifted
            ? parsedDateTime(m.stopdate_shifted) >= parsedDateTime(from)
            : true;
        return (isAfterFrom || !from) && (isBeforeTo || !to);
      })) ||
    [];

  const today = new Date();
  const sortedMeds = unsortedMeds?.length
    ? sortMedications(unsortedMeds, sortParams)
    : [];
  const stoppedMeds = sortedMeds?.length
    ? getStoppedMedications(sortedMeds, today)
    : [];
  const currentMeds = sortedMeds?.length
    ? getActiveMedications(sortedMeds, today)
    : [];
  return currentMeds.concat(stoppedMeds);
};

export const orderingPhysicianLabelFromMedOrder = (order) => {
  const {
    orderingphysicianfirstname: firstName,
    orderingphysicianlastname: lastName,
    orderingphysiciancredentials: credentials,
  } = order;

  return `${lastName}, ${firstName}${credentials ? `, ${credentials}` : ''}`;
};

export const getCoveredLabel = (covered) => {
  switch (covered) {
    case 'true':
      return <FormattedMessage {...messages.coveredYes} />;
    case 'false':
      return <FormattedMessage {...messages.coveredNo} />;
    default:
      return null;
  }
};

/**
 * Helper function that will calculate the date X days prior to the passed in Date and.
 * return the ISO String of the calculated date. If no date is passed in, today's date is used.
 * @param {*} days number of days to subtracked from date.
 * @param {*} date date to count back from.
 * @returns ISO String format of the calculated Date.
 */
export const getDateXDaysBeforeDateAsISOString = (days, date = new Date()) => {
  const daysBeforeInMs = days * MILLISECONDS_IN_DAY;
  const daysBefore = new Date(date.getTime());

  daysBefore.setTime(date.getTime() - daysBeforeInMs);
  return daysBefore.toISOString();
};

export const isClearscriptsMaintStatusResponseOK = (res) => {
  if (res?.status === HTTP_STATUS_OK) return true;
  if (res?.id) return true;
  return false;
};

export const isAPIStatusOK = (res) => {
  if (res && res.status && res.status === HTTP_STATUS_OK) return true;
  return false;
};

export const isNeedsApprovalMed = (m) =>
  m.medattestation &&
  m.medattestation === STRING_TRUE &&
  (!m.reconcileddate || m.reconcileddate === '') &&
  (!m.medatadmit || m.medatadmit !== STRING_TRUE);

/**
 * Given a set of buildProps containing a medCardDTO obj, generate the Start Med Order Data
 * object for that medication.
 * @param {*} buildProps
 * @returns
 */
export const buildMedStartOrderConfigObj = (buildProps) => {
  const {
    medication,
    patient,
    startorderDTO,
    stoporderDTO,
    isMedFromDoctor,
  } = buildProps;

  if (!medication || !startorderDTO) {
    return null;
  }
  // case when reconciledPhysician is not the reconciled user
  // stripping - sign from number to account for mumms users w/ -personnumbers
  const { isHardCopySig } = startorderDTO;
  const { isprescription } = medication;

  const medAtAdmit =
    startorderDTO.medorderorigintype === MAINT_ORDER_TYPE_ADMIT;
  const medAttestation =
    startorderDTO.medorderorigintype === MAINT_ORDER_TYPE_VERBAL;
  const showMedsAtAdmitMSG = medAtAdmit || (!medAtAdmit && !medAttestation);

  const showPreparerMSG = !showMedsAtAdmitMSG && !isMedFromDoctor;

  const isMedBeforeAdmit =
    !isprescription &&
    medication.startdate_shifted &&
    patient.admittedDate &&
    moment(parseDateMMDDYYYYSlashes(medication.startdate_shifted)).isBefore(
      moment(new Date(patient.admittedDate).toISOString()),
    );

  const isReconciled = !medAtAdmit && !!startorderDTO.approvinguserpersonid;

  const approvalDateTime =
    isReconciled && !!startorderDTO.approvaldatetime
      ? formatLocalDate(new Date(startorderDTO.approvaldatetime).toISOString())
      : '';

  const showHardCopySigMsg =
    !showMedsAtAdmitMSG && isReconciled && isHardCopySig;

  const startorderConfigs = {
    pageBreak: !!stoporderDTO,
    doStartOrder: true,
    doStopOrder: false,
    orderDetails: startorderDTO,
    isHardCopySig,
    showMedsAtAdmitMSG,
    medAtAdmit,
    medAttestation,
    isReconciled,
    showPreparerMSG,
    isMedBeforeAdmit,
    approvalDateTime,
    showHardCopySigMsg,
  };

  return startorderConfigs;
};

// TODO: update fields releated to stop med order approvals once that work goes
// live.
export const buildMedStopOrderConfigObj = (buildProps) => {
  const { medication, patient, stoporderDTO, isMedFromDoctor } = buildProps;
  const { isprescription } = medication;

  const { isHardCopySig } = stoporderDTO;

  const medAtAdmit = stoporderDTO.medorderorigintype === MAINT_ORDER_TYPE_ADMIT;
  const medAttestation =
    stoporderDTO.medorderorigintype === MAINT_ORDER_TYPE_VERBAL;
  const showMedsAtAdmitMSG = medAtAdmit || (!medAtAdmit && !medAttestation);

  const showPreparerMSG = !showMedsAtAdmitMSG && !isMedFromDoctor;

  const isMedBeforeAdmit =
    !isprescription &&
    medication.startdate_shifted &&
    patient.admittedDate &&
    moment(parseDateMMDDYYYYSlashes(medication.startdate_shifted)).isBefore(
      moment(new Date(patient.admittedDate).toISOString()),
    );

  const isReconciled = !medAtAdmit && !!stoporderDTO.approvinguserpersonid;

  const approvalDateTime =
    isReconciled && !!stoporderDTO.approvaldatetime
      ? formatLocalDate(new Date(stoporderDTO.approvaldatetime).toISOString())
      : '';

  const showHardCopySigMsg =
    !showMedsAtAdmitMSG && isReconciled && isHardCopySig;

  const stoporderConfigs = {
    pageBreak: false,
    doStartOrder: false,
    doStopOrder: true,
    orderDetails: stoporderDTO,
    isHardCopySig,
    showMedsAtAdmitMSG,
    medAtAdmit,
    medAttestation,
    isReconciled,
    showPreparerMSG,
    isMedBeforeAdmit,
    approvalDateTime,
    showHardCopySigMsg,
  };
  return stoporderConfigs;
};

/*
 * check the pnList for notes with entity type MMD, which is a med assocaited with a med order.
 * If the entitykey for the progressnote matches the id of the medication id, this med is
 * from an order.
 */
export const showStartReason = (med) => {
  if (!med) {
    return false;
  }
  if (med.stopreason === RCOPIA_STOPREASON_DELETED) {
    return false;
  }
  const today = new Date();
  const isStopped =
    med.iscurrentversion === MED_IS_CURRENT_VERSION_FALSE ||
    (med.stopdate_shifted && today > new Date(med.stopdate_shifted));
  return !isStopped;
};

export const showMedsOrderIcon = (med, pnList) => {
  if (
    !med ||
    med.stopreason === RCOPIA_STOPREASON_DELETED ||
    !pnList ||
    pnList.length === 0
  ) {
    return false;
  }

  return pnList.some((pn) => {
    if (
      pn &&
      pn.patientprogressnoteentitydto &&
      pn.patientprogressnoteentitydto.MMD &&
      pn.patientprogressnoteentitydto.MMD
    ) {
      const mmdList = pn.patientprogressnoteentitydto.MMD;
      return Array.isArray(mmdList)
        ? mmdList.some((entity) => entity.entitykey === med.id)
        : false;
    }
    return false;
  });
};
